import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation, Trans } from 'react-i18next';
import LocalizedLink from '../components/LocalizedLink.js';
import Seo from '../components/Seo.js';
import { useLocalizedPath } from '../utils/localizedPath.js';
import { buildCanonicalUrl } from '../config/seo.js';

const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4'];

function Home() {
    const { t } = useTranslation();
    const { localize } = useLocalizedPath();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Localized structured data: kept in-app (rather than only in the
    // static index.html) so JS-rendering crawlers see schema that
    // matches the language actually on the page, not just English.
    const webAppSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Kris Kindler',
        applicationCategory: 'EventApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        operatingSystem: 'Any',
        description: t('seo.description'),
        url: buildCanonicalUrl(localize('/')),
        author: { '@type': 'Organization', name: 'Kris Kindler' },
        featureList: [
            t('home.features.flexible.title'),
            t('home.features.gifts.title'),
            t('home.features.reusable.title'),
        ],
    };

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_KEYS.map((key) => ({
            '@type': 'Question',
            name: t(`home.faq.${key}.question`),
            acceptedAnswer: {
                '@type': 'Answer',
                text: t(`home.faq.${key}.answer`),
            },
        })),
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const closeDropdown = (event) => {
        if (!event.target.closest('#language-dropdown-container')) {
            setIsDropdownOpen(false);
        };
    };

    useEffect(() => {
        window.addEventListener('click', closeDropdown);
        return () => {
            window.removeEventListener('click', closeDropdown);
        };
    }, []);

    return (
        <main>
            <Seo path="/" title={t('seo.title')} description={t('seo.description')} keywords={t('seo.keywords')} />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(webAppSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Helmet>

            {/* Hero Section */}
            <header className="hero-section text-white py-20 sm:py-32">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h1 className="text-4xl sm:text-6xl font-extrabold mb-4 drop-shadow-lg">
                        {t('home.hero.title')}
                    </h1>
                    <p className="text-xl sm:text-2xl font-light mb-8 max-w-3xl mx-auto opacity-90">
                        <Trans i18nKey="home.hero.subtitle" />
                    </p>
                    <LocalizedLink to="/signup" className="cta-button inline-flex items-center justify-center px-10 py-4 border border-transparent text-lg font-bold rounded-full text-red-600 bg-white hover:bg-gray-100 shadow-xl transition transform">
                        {t('home.hero.cta')}
                        {/* Icon for immediate action */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </LocalizedLink>
                    <p className="mt-4 text-sm opacity-80">{t('home.hero.quickSetup')}</p>
                </div>
            </header>

            {/* Value Proposition / Key Features Section */}
            <section className="py-16 sm:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl font-extrabold text-red-600 mb-12">
                        {t('home.features.title')}
                    </h2>

                    <div className="grid md:grid-cols-3 gap-10">

                        {/* Feature 1: Rule-Based Assignment System */}
                        <div className="bg-zinc-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-[1.02]">
                            <div className="text-green-700 mb-4 inline-block p-3 bg-green-100 rounded-full">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2l4-4m5.618-4.618a1.5 1.5 0 012.12 2.121L12.12 18.12a1.5 1.5 0 01-2.12 0l-5.618-5.619a1.5 1.5 0 012.12-2.121z" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold mb-3 text-gray-900">{t('home.features.flexible.title')}</h4>
                            <p className="text-gray-600">
                                <Trans i18nKey="home.features.flexible.description" />
                            </p>
                        </div>

                        {/* Feature 2: Age/Sex-Based Gift Suggestions (Monetization) */}
                        <div className="bg-zinc-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-[1.02]">
                            <div className="text-red-600 mb-4 inline-block p-3 bg-red-100 rounded-full">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0a9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold mb-3 text-gray-900">{t('home.features.gifts.title')}</h4>
                            <p className="text-gray-600">
                                <Trans i18nKey="home.features.gifts.description" />
                            </p>
                        </div>

                        {/* Feature 3: Reusable Templates & Notifications */}
                        <div className="bg-zinc-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-[1.02]">
                            <div className="text-blue-600 mb-4 inline-block p-3 bg-blue-100 rounded-full">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.001 8.001 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold mb-3 text-gray-900">{t('home.features.reusable.title')}</h4>
                            <p className="text-gray-600">
                                <Trans i18nKey="home.features.reusable.description" />
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SEO-Rich Content Section */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        {t('home.seoContent.title')}
                    </h2>
                    <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                        <p>
                            <Trans i18nKey="home.seoContent.paragraph1" />
                        </p>
                        <p>
                            <Trans i18nKey="home.seoContent.paragraph2" />
                        </p>
                        <p>
                            <Trans i18nKey="home.seoContent.paragraph3" />
                        </p>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-16 sm:py-24 bg-red-50 festive-bg-pattern">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl sm:text-4xl font-extrabold text-red-700 text-center mb-12">
                        {t('home.howItWorks.title')}
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8 items-start">

                        {/* Step 1 */}
                        <div className="text-center">
                            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-red-600 text-white rounded-full text-xl font-bold">1</div>
                            <h4 className="text-xl font-bold mb-2 text-gray-900">{t('home.howItWorks.step1.title')}</h4>
                            <p className="text-gray-600">
                                {t('home.howItWorks.step1.description')}
                            </p>
                        </div>

                        {/* Step 2 */}
                        <div className="text-center">
                            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-red-600 text-white rounded-full text-xl font-bold">2</div>
                            <h4 className="text-xl font-bold mb-2 text-gray-900">{t('home.howItWorks.step2.title')}</h4>
                            <p className="text-gray-600">
                                <Trans i18nKey="home.howItWorks.step2.description" />
                            </p>
                        </div>

                        {/* Step 3 */}
                        <div className="text-center">
                            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-red-600 text-white rounded-full text-xl font-bold">3</div>
                            <h4 className="text-xl font-bold mb-2 text-gray-900">{t('home.howItWorks.step3.title')}</h4>
                            <p className="text-gray-600">
                                {t('home.howItWorks.step3.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
                        {t('home.faq.title')}
                    </h2>
                    <div className="space-y-4">
                        <details className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                            <summary className="font-bold text-lg cursor-pointer text-gray-900 flex items-center justify-between">
                                {t('home.faq.q1.question')}
                                <span className="ml-2 text-red-600 group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <p className="mt-4 text-gray-700 leading-relaxed">
                                {t('home.faq.q1.answer')}
                            </p>
                        </details>

                        <details className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                            <summary className="font-bold text-lg cursor-pointer text-gray-900 flex items-center justify-between">
                                {t('home.faq.q2.question')}
                                <span className="ml-2 text-red-600 group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <p className="mt-4 text-gray-700 leading-relaxed">
                                {t('home.faq.q2.answer')}
                            </p>
                        </details>

                        <details className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                            <summary className="font-bold text-lg cursor-pointer text-gray-900 flex items-center justify-between">
                                {t('home.faq.q3.question')}
                                <span className="ml-2 text-red-600 group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <p className="mt-4 text-gray-700 leading-relaxed">
                                {t('home.faq.q3.answer')}
                            </p>
                        </details>

                        <details className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                            <summary className="font-bold text-lg cursor-pointer text-gray-900 flex items-center justify-between">
                                {t('home.faq.q4.question')}
                                <span className="ml-2 text-red-600 group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <p className="mt-4 text-gray-700 leading-relaxed">
                                {t('home.faq.q4.answer')}
                            </p>
                        </details>
                    </div>
                </div>
            </section>

            {/* CTA Block (Repeat) */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('home.cta.title')}</h2>
                    <p className="text-lg text-gray-600 mb-8">{t('home.cta.subtitle')}</p>
                    <LocalizedLink to="/signup" className="cta-button inline-flex items-center justify-center px-12 py-4 border border-transparent text-xl font-bold rounded-full text-white bg-green-700 hover:bg-green-800 shadow-xl transition transform">
                        {t('home.cta.button')}
                    </LocalizedLink>
                </div>
            </section>
        </main>
    );
}

export default Home;